"use client"

import { useState, useContext, useEffect } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  alpha,
  Alert,
  Fade,
  useTheme,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import PaymentIcon from "@mui/icons-material/Payment"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import RefreshIcon from "@mui/icons-material/Refresh"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import SaveIcon from "@mui/icons-material/Save"
import CloseIcon from "@mui/icons-material/Close"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import TablaMedioDePagoComponent from "./TablaMedioDePagoComponent.js"

const GestionMedioPago = () => {
  const theme = useTheme()
  const {
    postTipoMedioDePago,
    getTipoMedioDePago,
    tipoMedioDePago,
    postMedioDePago,
    updateTipoMedioDePago,
    deleteTipoMedioDePago,
    isNavbarCollapsed,
  } = useContext(ErpContext)

  // Estados para tipo medio de pago
  const [descripcionMedioPago, setDescripcionMedioPago] = useState("")
  const [verEnCajaMedioPago, setVerEnCajaMedioPago] = useState(false)
  const [verEnTransaccionMedioPago, setVerEnTransaccionMedioPago] = useState(false)
  const [afectaCuentaCorriente, setAfectaCuentaCorriente] = useState(false)

  // Estados para medio de pago
  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState("")
  const [nuevoMedioDePago, setNuevoMedioDePago] = useState("")

  // Estados generales
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState("")
  
  // Estados para modal de edici?n de tipos
  const [editTipoModalOpen, setEditTipoModalOpen] = useState(false)
  const [editingTipo, setEditingTipo] = useState(null)
  const [editTipoDescripcion, setEditTipoDescripcion] = useState("")
  const [editTipoVerEnCaja, setEditTipoVerEnCaja] = useState(false)
  const [editTipoVerEnTransaccion, setEditTipoVerEnTransaccion] = useState(false)
  const [editTipoAfectaCuentaCorriente, setEditTipoAfectaCuentaCorriente] = useState(false)
  const [editTipoCrearEnCuentaCorriente, setEditTipoCrearEnCuentaCorriente] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      await getTipoMedioDePago()
    } catch (err) {
      console.error("Error al cargar datos:", err)
      setError("No se pudieron cargar los datos. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleTipoMedioDePagoSubmit = async (e) => {
    e.preventDefault()
    if (!descripcionMedioPago.trim()) {
      setError("Por favor ingrese una descripci?n para el tipo de medio de pago")
      return
    }

    setLoading(true)
    try {
      await postTipoMedioDePago(
        descripcionMedioPago,
        verEnCajaMedioPago,
        verEnTransaccionMedioPago,
        false, // Multi moneda: eliminado
        false, // Pago diferido: eliminado
        false  // Selecci?n por usuario: eliminado
      )
      // Limpiar formulario
      setDescripcionMedioPago("")
      setVerEnCajaMedioPago(false)
      setVerEnTransaccionMedioPago(false)
      setAfectaCuentaCorriente(false)
      
      setSuccessMessage("Tipo de medio de pago agregado correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error("Error al agregar tipo medio de pago:", err)
      setError("No se pudo agregar el tipo de medio de pago. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleMedioDePagoSubmit = async (e) => {
    e.preventDefault()
    if (!selectedTipoMedioDePago || !nuevoMedioDePago.trim()) {
      setError("Por favor complete todos los campos para el medio de pago")
      return
    }

    // Validar que no se pueda crear medios de pago con tipo 9 (Mis Pagos - usado por MercadoPago)
    if (parseInt(selectedTipoMedioDePago) === 9) {
      setError("No se pueden crear medios de pago adicionales para el tipo 'Mis Pagos' (ID 9). Este tipo est? reservado para la integraci?n con MercadoPago y ya tiene su medio de pago configurado en el sistema.")
      return
    }

    setLoading(true)
    try {
      await postMedioDePago(selectedTipoMedioDePago, nuevoMedioDePago)
      setSelectedTipoMedioDePago("")
      setNuevoMedioDePago("")
      setSuccessMessage("Medio de pago agregado correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error("Error al agregar medio de pago:", err)
      setError("No se pudo agregar el medio de pago. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleTipoEditStart = (tipo) => {
    setError(null)
    
    // Validar que no se pueda editar el tipo 9 (Mis Pagos - usado por MercadoPago)
    if (tipo.id === 9) {
      setError("No se puede editar el tipo de medio de pago 'Mis Pagos' (ID 9) porque est? reservado para la integraci?n con MercadoPago")
      return
    }
    
    setEditingTipo(tipo)
    setEditTipoDescripcion(tipo.descripcion)
    setEditTipoVerEnCaja(tipo.verEnCaja)
    setEditTipoVerEnTransaccion(tipo.verEnTransaccion)
    setEditTipoAfectaCuentaCorriente(tipo.afectaCuentaCorriente || false)
    setEditTipoCrearEnCuentaCorriente(tipo.crearEnCuentaCorriente !== undefined ? tipo.crearEnCuentaCorriente : true)
    setEditTipoModalOpen(true)
  }

  const handleTipoEditCancel = () => {
    setEditTipoModalOpen(false)
    setEditingTipo(null)
    setEditTipoDescripcion("")
    setEditTipoVerEnCaja(false)
    setEditTipoVerEnTransaccion(false)
    setEditTipoAfectaCuentaCorriente(false)
    setEditTipoCrearEnCuentaCorriente(false)
  }

  const handleTipoEditSave = async () => {
    if (!editTipoDescripcion.trim()) {
      setError("Por favor ingrese una descripci?n para el tipo")
      return
    }

    setLoading(true)
    try {
      await updateTipoMedioDePago(editingTipo.id, {
        descripcion: editTipoDescripcion.trim(),
        verEnCaja: editTipoVerEnCaja,
        verEnTransaccion: editTipoVerEnTransaccion,
        esMultiMoneda: false,   // Multi moneda: eliminado
        esPagoDiferido: false,  // Pago diferido: eliminado
        userSelect: false,      // Selecci?n por usuario: eliminado
        afectaCuentaCorriente: editTipoAfectaCuentaCorriente,
        crearEnCuentaCorriente: editTipoCrearEnCuentaCorriente,
      })
      setSuccessMessage("Tipo actualizado correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
      handleTipoEditCancel()
    } catch (err) {
      console.error("Error al actualizar tipo:", err)
      setError("No se pudo actualizar el tipo. Intente nuevamente.")
    } finally {
      setLoading(false)
    }
  }

  const handleTipoDelete = async (tipoId) => {
    // Validar que no se pueda eliminar el tipo 9 (Mis Pagos - usado por MercadoPago)
    if (tipoId === 9) {
      setError("No se puede eliminar el tipo de medio de pago 'Mis Pagos' (ID 9) porque est? reservado para la integraci?n con MercadoPago")
      return
    }
    
    const confirmDelete = window.confirm("?Est? seguro de eliminar este tipo de medio de pago?")
    if (!confirmDelete) return

    setLoading(true)
    try {
      await deleteTipoMedioDePago(tipoId)
      setSuccessMessage("Tipo eliminado correctamente")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error("Error al eliminar tipo:", err)
      const errorMessage = err?.response?.data?.message || err?.message || "No se pudo eliminar el tipo"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }


  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
      {/* Encabezado principal */}
      <Card
        elevation={3}
        sx={{
          mb: 2,
          borderRadius: 2,
          backgroundColor: "#5b5b5f",
          "&:hover": {
            backgroundColor: alpha("#5b5b5f", 0.9),
          },
          color: "white",
        }}
      >
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <PaymentIcon
              sx={{
                fontSize: 24,
                color: "rgb(244, 177, 14)",
                mr: 1,
              }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: "white", fontSize: "1.25rem" }}>
              Gestión de Tipos y Medios de Pago
            </Typography>
          </Box>
          <Typography variant="body2" color="white" sx={{ ml: 4.5, fontSize: "0.8rem" }}>
            Administre los tipos de medios de pago y sus medios de pago asociados
          </Typography>
        </CardContent>
      </Card>

      {/* Alertas - Posición fija abajo */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 1.5,
            boxShadow: 3,
            py: 0.5,
            fontSize: "0.85rem",
            minWidth: 300,
          }}
          onClose={() => setSuccess(false)}
        >
          {successMessage}
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 1.5,
            boxShadow: 3,
            py: 0.5,
            fontSize: "0.85rem",
            minWidth: 300,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Crear Medio de Pago - Arriba full width */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              height: "fit-content",
            }}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CreditCardIcon
                  sx={{
                    fontSize: 18,
                    color: "rgb(244, 177, 14)",
                    mr: 1,
                  }}
                />
                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  Crear Medio de Pago
                </Typography>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <form onSubmit={handleMedioDePagoSubmit}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel id="tipo-medio-pago-label" sx={{ fontSize: "0.875rem" }}>
                        Tipo de Medio de Pago
                      </InputLabel>
                      <Select
                        labelId="tipo-medio-pago-label"
                        value={selectedTipoMedioDePago}
                        onChange={(e) => setSelectedTipoMedioDePago(e.target.value)}
                        label="Tipo de Medio de Pago"
                        disabled={loading}
                        sx={{
                          borderRadius: 1.5,
                          fontSize: "0.875rem",
                        }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: "0.875rem" }}>
                          Seleccione un tipo
                        </MenuItem>
                        {tipoMedioDePago.map((tipo) => (
                          <MenuItem key={tipo.id} value={tipo.id} sx={{ fontSize: "0.875rem" }}>
                            {tipo.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Descripción del Medio de Pago"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={nuevoMedioDePago}
                      onChange={(e) => setNuevoMedioDePago(e.target.value)}
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1.5,
                          fontSize: "0.875rem",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CreditCardIcon color="action" sx={{ fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        textTransform: "none",
                        boxShadow: 1,
                        py: 0.75,
                        px: 3,
                        fontSize: "0.85rem",
                        backgroundColor: "#5b5b5f",
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Agregar Medio de Pago
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Crear Tipo de Medio de Pago y Tabla Medios de Pago - Abajo lado a lado */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {/* Crear Tipo de Medio de Pago - 40% */}
        <Grid item xs={12} lg={5} sx={{ flexBasis: { lg: '40%' }, maxWidth: { lg: '40%' } }}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              height: "fit-content",
            }}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AccountBalanceWalletIcon
                  sx={{
                    fontSize: 18,
                    color: "rgb(244, 177, 14)",
                    mr: 1,
                  }}
                />
                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  Crear Tipo de Medio de Pago
                </Typography>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <form onSubmit={handleTipoMedioDePagoSubmit}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      label="Descripción del Tipo"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={descripcionMedioPago}
                      onChange={(e) => setDescripcionMedioPago(e.target.value)}
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1.5,
                          fontSize: "0.875rem",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PaymentIcon color="action" sx={{ fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={verEnCajaMedioPago}
                            onChange={(e) => setVerEnCajaMedioPago(e.target.checked)}
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Ver en Caja</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={verEnTransaccionMedioPago}
                            onChange={(e) => setVerEnTransaccionMedioPago(e.target.checked)}
                            color="primary"
                            disabled={loading}
                          />
                        }
                        label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Ver en Transacción</Typography>}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
                      fullWidth
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        textTransform: "none",
                        boxShadow: 1,
                        py: 0.75,
                        fontSize: "0.85rem",
                        backgroundColor: "#5b5b5f",
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Agregar Tipo
                    </Button>
                  </Grid>
                </Grid>
              </form>

              {/* Lista de tipos existentes */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#2d3748", fontSize: "0.85rem" }}>
                  Tipos Existentes ({tipoMedioDePago.length})
                </Typography>
                <Box sx={{ maxHeight: 250, overflow: "auto" }}>
                  <List dense>
                    {tipoMedioDePago.map((tipo) => (
                      <ListItem
                        key={tipo.id}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          backgroundColor: "#f8f9fa",
                          py: 0.5,
                          "&:hover": {
                            backgroundColor: "#E3EAFD",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <AccountBalanceWalletIcon sx={{ color: "rgb(244, 177, 14)", fontSize: 18 }} />
                        </ListItemIcon>
                        <Box sx={{ flexGrow: 1, mr: 1 }}>
                          <ListItemText
                            primary={tipo.descripcion}
                            primaryTypographyProps={{
                              fontWeight: 500,
                              fontSize: "0.85rem",
                            }}
                          />
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mr: 0.5 }}>
                          {tipo.verEnCaja && (
                            <Chip label="Caja" size="small" sx={{ fontSize: "0.65rem", height: 18 }} />
                          )}
                          {tipo.verEnTransaccion && (
                            <Chip label="Trans." size="small" sx={{ fontSize: "0.65rem", height: 18 }} />
                          )}
                        </Box>
                        <Chip
                          label={`ID: ${tipo.id}`}
                          size="small"
                          sx={{
                            backgroundColor: alpha("#5b5b5f", 0.1),
                            color: "#5b5b5f",
                            fontSize: "0.7rem",
                            height: 18,
                          }}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", ml: 0.5, gap: 0.5 }}>
                          <Tooltip title="Editar tipo">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleTipoEditStart(tipo)}
                                disabled={loading}
                                sx={{
                                  color: "rgb(244, 177, 14)",
                                  bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                                  p: 0.5,
                                  "&:hover": {
                                    bgcolor: alpha("rgb(244, 177, 14)", 0.2),
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Eliminar tipo">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleTipoDelete(tipo.id)}
                                disabled={loading}
                                sx={{
                                  color: "#d32f2f",
                                  bgcolor: alpha("#d32f2f", 0.1),
                                  p: 0.5,
                                  "&:hover": {
                                    bgcolor: alpha("#d32f2f", 0.2),
                                  },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabla de Medios de Pago - 60% */}
        <Grid item xs={12} lg={7} sx={{ flexBasis: { lg: '60%' }, maxWidth: { lg: '60%' } }}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                  <CreditCardIcon
                    sx={{
                      fontSize: 18,
                      color: "rgb(244, 177, 14)",
                      mr: 1,
                    }}
                  />
                  <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    Medios de Pago
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                  onClick={fetchData}
                  disabled={loading}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    minWidth: 100,
                    py: 0.5,
                    fontSize: "0.8rem",
                    borderColor: "rgb(244, 177, 14)",
                    color: "rgb(244, 177, 14)",
                    "&:hover": {
                      borderColor: alpha("rgb(244, 177, 14)", 0.8),
                      backgroundColor: alpha("rgb(244, 177, 14)", 0.05),
                    },
                  }}
                >
                  Actualizar
                </Button>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <TablaMedioDePagoComponent />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Modal de Edición de Tipo */}
      <Dialog
        open={editTipoModalOpen}
        onClose={handleTipoEditCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1.5,
            boxShadow: 24,
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#5b5b5f",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            py: 1,
            px: 1.5,
          }}
        >
          <EditIcon sx={{ fontSize: 18 }} />
          <Typography variant="subtitle1" sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
            Editar Tipo de Medio de Pago
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 1.5, px: 1.5, py: 1.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField
              label="Descripción"
              variant="outlined"
              fullWidth
              size="small"
              value={editTipoDescripcion}
              onChange={(e) => setEditTipoDescripcion(e.target.value)}
              disabled={loading}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  fontSize: "0.875rem",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PaymentIcon color="action" sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={editTipoVerEnCaja}
                    onChange={(e) => setEditTipoVerEnCaja(e.target.checked)}
                    color="primary"
                    disabled={loading}
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Ver en Caja</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={editTipoVerEnTransaccion}
                    onChange={(e) => setEditTipoVerEnTransaccion(e.target.checked)}
                    color="primary"
                    disabled={loading}
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Ver en Transacción</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={editTipoAfectaCuentaCorriente}
                    onChange={(e) => setEditTipoAfectaCuentaCorriente(e.target.checked)}
                    color="primary"
                    disabled={loading}
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Afecta Cuenta Corriente</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={editTipoCrearEnCuentaCorriente}
                    onChange={(e) => setEditTipoCrearEnCuentaCorriente(e.target.checked)}
                    color="primary"
                    disabled={loading}
                  />
                }
                label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Crear en Cuenta Corriente</Typography>}
              />
            </Box>

            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0.5, fontSize: "0.85rem" }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, gap: 1 }}>
          <Button
            onClick={handleTipoEditCancel}
            disabled={loading}
            size="small"
            startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
            sx={{
              color: "#6b6b6b",
              fontSize: "0.85rem",
              "&:hover": {
                backgroundColor: alpha("#6b6b6b", 0.1),
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleTipoEditSave}
            disabled={loading}
            variant="contained"
            size="small"
            startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
            sx={{
              backgroundColor: "#5b5b5f",
              fontSize: "0.85rem",
              "&:hover": {
                backgroundColor: alpha("#5b5b5f", 0.9),
              },
            }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default GestionMedioPago
